// Copyright 1999-2017. Plesk International GmbH. All rights reserved.
/* eslint-disable react/prop-types */

import { createElement, isValidElement } from '../../utils';
import PropTypes from 'prop-types';
import { CLS_PREFIX } from '../../constants';
import Translate from '../Translate';
import Button from '../Button';
import Toolbar, { ToolbarGroup, ToolbarExpander } from '../Toolbar';
import Dropdown from '../Dropdown';
import SegmentedControl from '../SegmentedControl';
import Menu, { MenuItem } from '../Menu';
import Checkbox from '../Checkbox';
import Badge from '../Badge';
import { VIEW_RESPONSIVE, VIEW_ROW, VIEW_TILE, VIEW_EXTENDED } from './CardList';

const renderSelection = ({ onSelectAll, onSelectNone, checked, badge, total, menuItems = [] }) => {
    let button = (
        <Button caret>
            <Checkbox
                checked={checked}
                onChange={isChecked => {
                    if (isChecked) {
                        onSelectAll();
                    } else {
                        onSelectNone();
                    }
                }}
                onClick={e => e.stopPropagation()}
            />
        </Button>
    );

    if (badge) {
        if (typeof badge === 'string') {
            badge = { label: badge };
        }

        button = <Badge {...badge}>{button}</Badge>;
    }

    menuItems = [
        <MenuItem
            key="selectAll"
            // eslint-disable-next-line react/jsx-handler-names
            onClick={onSelectAll}
            label={total}
        >
            <Translate content="Card.selectAll" fallback="Select all" />
        </MenuItem>,
        <MenuItem
            key="selectNone"
            // eslint-disable-next-line react/jsx-handler-names
            onClick={onSelectNone}
        >
            <Translate content="Card.selectNone" fallback="Select none" />
        </MenuItem>,
    ].concat(menuItems);

    return (
        <ToolbarGroup key="selection" title={<Translate content="CardList.toolbarSelect" fallback="Select" />}>
            <Dropdown menu={<Menu>{menuItems}</Menu>}>
                {button}
            </Dropdown>
        </ToolbarGroup>
    );
};

const renderView = (onViewChange, currentView) => {
    const viewButtons = [
        {
            value: VIEW_EXTENDED,
            title: <Translate content="CardList.viewCards" fallback="Cards" />,
            icon: 'site-page',
        },
        {
            value: VIEW_TILE,
            title: <Translate content="CardList.viewTiles" fallback="Tiles" />,
            icon: 'four-squares',
        },
        {
            value: VIEW_ROW,
            title: <Translate content="CardList.viewList" fallback="List" />,
            icon: 'list',
        },
    ];

    return (
        <ToolbarGroup key="view" title={<Translate content="CardList.toolbarView" fallback="View" />}>
            <SegmentedControl
                buttons={viewButtons}
                selected={currentView}
                onChange={view => onViewChange(view)}
            />
        </ToolbarGroup>
    );
};

const isToolbarExpander = element => isValidElement(element) && element.type === ToolbarExpander;

const CardListToolbar = ({ baseClassName, selection, onViewChange, currentView, groups }) => {
    let toolbar = [];

    if (selection) {
        toolbar.push(renderSelection(selection));
    }

    toolbar = toolbar.concat(groups);

    if (onViewChange && currentView !== VIEW_RESPONSIVE) {
        const hasExpander = groups.filter(isToolbarExpander).length > 0;

        if (!hasExpander) {
            toolbar.push(<ToolbarExpander key="expander" />);
        }

        toolbar.push(renderView(onViewChange, currentView));
    }

    if (toolbar.length === 0) {
        return null;
    }

    return (
        <Toolbar className={baseClassName}>
            {toolbar}
        </Toolbar>
    );
};

CardListToolbar.propTypes = {
    selection: PropTypes.object,
    groups: PropTypes.arrayOf(
        PropTypes.element,
    ).isRequired,
    onViewChange: PropTypes.func,
    currentView: PropTypes.string.isRequired,
    baseClassName: PropTypes.string,
};
CardListToolbar.defaultProps = {
    selection: null,
    onViewChange: null,
    baseClassName: `${CLS_PREFIX}card-list__toolbar`,
};

export default CardListToolbar;



// WEBPACK FOOTER //
// ./components/CardList/CardListToolbar.js